import {
  GetStaticComponentProps,
  LinkField,
  useSitecoreContext,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { useTheme } from 'lib/context/ThemeContext';
import { mapItemFieldResultsToObject } from 'lib/graphql/mappers/map-item-field-results-to-object';
import { mapSearchResults } from 'lib/graphql/mappers/map-search-results';
import { IntegratedGraphQlResult } from 'lib/graphql/types/integrated-graphql-result';
import { ItemFieldResult } from 'lib/graphql/types/item-field-result';
import { ItemSearchResults } from 'lib/graphql/types/item-search-results';
import { getEnum } from 'lib/utils/get-enum';
import { getHeadingLevel } from 'lib/utils/getHeadingLevel';
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import Component, { ComponentSpacing } from 'src/helpers/Component/Component';
import Headline from 'src/helpers/Headline/Headline';
import LinkWrapper from 'src/helpers/LinkWrapper/LinkWrapper';
import ImagePrimary from 'src/helpers/Media/ImagePrimary';
import { Subheadline } from 'src/helpers/Subheadline';
import SvgIcon, { IconTypes } from 'src/helpers/SvgIcon/SvgIcon';
import { HeroWithNavigationTheme } from './HeroWithNavigation.theme';
import { useMemo } from 'react';

export type HeroWithNavigationLink =
  Feature.EnterpriseWeb.Enterprise.Components.Hero.HeroWithNavigation.HeroWithNavigationLink;

export type HeroWithNavigationProps =
  Feature.EnterpriseWeb.Enterprise.Components.Hero.HeroWithNavigation.HeroWithNavigation & {
    fields: {
      children: HeroWithNavigationLink[];
    };
  };
const HeroWithNavigation = (props: HeroWithNavigationProps) => {
  const { fields } = props;
  const compFields: any = useMemo(() => {
    if (fields?.data) {
      const mappedFields = fields.data.item.fields.map((field: any) => {
        return { [field.name]: field.jsonValue };
      });
      const result = Object.assign({}, ...mappedFields);
      const mappedChildrenFields = fields.data.item.children?.results?.map((field: any) => {
        return {
          fields: Object.assign(
            {},
            ...field?.fields?.map((innerField: any) => {
              return { [innerField.name]: innerField.jsonValue };
            })
          ),
        };
      });

      result.children = mappedChildrenFields;

      return result;
    }

    return fields;
  }, [fields?.data]);

  const propFields: any = useMemo(() => {
    if (compFields !== undefined && 'children' in compFields) {
      return { fields: compFields };
    }
    return {};
  }, [compFields]);

  const spacing = getEnum<ComponentSpacing>(compFields?.componentSpacing) || 'standard';
  const spacingValue = spacing === 'standard' ? '8' : '4';
  const context = useSitecoreContext();
  const breadcrumb = (context && context.sitecoreContext?.breadcrumb) || [];
  const { themeData } = useTheme(HeroWithNavigationTheme(spacingValue));

  return (
    <Component
      dataComponent="hero/herowithnavigation"
      className={breadcrumb?.length === 1 ? 'relative pt-[55px] ml:pt-0' : 'relative'}
      {...props}
    >
      <div className="col-span-12">
        {compFields?.headlineText?.value && (
          <div className={classNames(themeData.classes.headlineContainer, 'md:grid-cols-12')}>
            <div className="col-span-12">
              <Headline
                useTag={getHeadingLevel('h1', compFields?.headlineLevel)}
                classes={themeData.classes.headline}
                {...propFields}
              />
            </div>
          </div>
        )}
      </div>
      <div className={classNames(themeData.classes.heroContainer, 'col-span-12')}>
        <ImagePrimary
          imageLayout="fill"
          ratio="hero"
          additionalDesktopClasses="h-[280px] ml:h-[422px]"
          additionalMobileClasses="h-[280px]"
          priority
          {...propFields}
        ></ImagePrimary>
        <div className={themeData.classes.linkContainer}>
          <Subheadline classes={themeData.classes.subheadlineStyle} {...propFields} />
          {compFields?.children?.map((_item: HeroWithNavigationLink, i: number) => {
            const _linkField = _item.fields?.navigationLink as LinkField;
            const _icon = getEnum<IconTypes>(_item.fields?.navigationIcon);

            return (
              <div key={i} className={themeData.classes.linkStyle}>
                <LinkWrapper
                  className={themeData.classes.linkWrapperStyle}
                  field={_linkField}
                  ariaLabel={{
                    value: (_item.fields?.navigationIcon?.name as string) || 'Navigation Icon',
                  }}
                >
                  {_item.fields?.navigationIcon && (
                    <div className={themeData.classes.svgWrapper}>
                      <SvgIcon icon={_icon} className={themeData.classes.svgIconStyle} />
                    </div>
                  )}
                </LinkWrapper>
              </div>
            );
          })}
        </div>
      </div>
    </Component>
  );
};

type IntegratedGraphQl = IntegratedGraphQlResult<{
  item: {
    fields: ItemFieldResult[];
    children: ItemSearchResults<{
      fields: ItemFieldResult[];
    }>;
  };
}>;

export const getStaticProps: GetStaticComponentProps = async (rendering) => {
  if (rendering.fields === undefined || !('data' in rendering.fields)) {
    return {};
  }

  const fields = rendering.fields as unknown as IntegratedGraphQl;

  const result = {
    fields: {
      ...mapItemFieldResultsToObject(fields.data.item.fields),
      children: mapSearchResults(fields.data.item.children, (child) => ({
        fields: {
          ...mapItemFieldResultsToObject(child.fields),
        },
      })),
    },
  };

  return result;
};

export default withDatasourceCheck()<HeroWithNavigationProps>(HeroWithNavigation);
